import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { notificationsApi } from '../api/notifications';
import useInboxStore from '../stores/inboxStore';

/**
 * Verbindet sich mit /notifications/inbox/stream (SSE) und invalidiert
 * react-query caches bei neuen Events. Zeigt optional einen Toast.
 *
 * Aktivierung: Komponente mountet → SSE startet. Unmount → SSE schliesst.
 * Wird typischerweise einmal in AppLayout / Header aufgehaengt.
 */
export function useInboxSSE({ enabled = true, onEvent } = {}) {
  const queryClient = useQueryClient();
  const incrementUnread = useInboxStore((s) => s.incrementUnread);
  const esRef = useRef(null);
  const reconnectRef = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof window === 'undefined' || typeof EventSource === 'undefined') {
      return undefined;
    }

    let cancelled = false;
    let backoffTimer = null;

    const connect = () => {
      if (cancelled) return;
      const url = notificationsApi.streamUrl();

      let es;
      try {
        es = new EventSource(url, { withCredentials: true });
      } catch {
        // Ungueltige URL o.ae. — einmal aufgeben.
        return;
      }
      esRef.current = es;

      es.addEventListener('inbox', (evt) => {
        try {
          const payload = JSON.parse(evt.data);
          if (payload?.type === 'created') {
            // Cache invalidieren
            queryClient.invalidateQueries({ queryKey: ['inbox'] });
            queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
            incrementUnread();
          }
          if (typeof onEvent === 'function') onEvent(payload);
        } catch {
          // ignore parse errors
        }
      });

      es.onopen = () => {
        reconnectRef.current = 0; // erfolgreich, backoff reset
      };

      es.onerror = () => {
        // Browser wird auto-reconnect versuchen; wir handeln nur den hard-close
        if (es.readyState === EventSource.CLOSED && !cancelled) {
          reconnectRef.current = Math.min(reconnectRef.current + 1, 6);
          const delay = Math.min(1000 * Math.pow(2, reconnectRef.current), 30_000);
          backoffTimer = setTimeout(() => {
            if (!cancelled) connect();
          }, delay);
        }
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (backoffTimer) clearTimeout(backoffTimer);
      if (esRef.current) {
        try { esRef.current.close(); } catch { /* ignore */ }
        esRef.current = null;
      }
    };
  }, [enabled, queryClient, incrementUnread, onEvent]);
}

export default useInboxSSE;
